import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["panel", "details", "analytics", "marketing", "launcher", "actions"]

  static STORAGE_KEY = "cookie_preferences"
  static LEGACY_STORAGE_KEY = "cookies_accepted"

  connect() {
    const preferences = this.preferences || this.migrateLegacyPreference()

    if (preferences) {
      this.syncInputs(preferences)
      this.showLauncher()
      this.dispatchChange(preferences)
    } else {
      this.showPanel()
    }
  }

  customize() {
    this.syncInputs(this.preferences || this.defaultPreferences)
    this.detailsTarget.hidden = false
    this.actionsTarget.hidden = true
    this.detailsTarget.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }

  acceptAll() {
    this.save({ analytics: true, marketing: true })
  }

  rejectAll() {
    this.save(this.defaultPreferences)
  }

  saveSelection() {
    this.save({
      analytics: this.analyticsTarget.checked,
      marketing: this.marketingTarget.checked
    })
  }

  open() {
    this.syncInputs(this.preferences || this.defaultPreferences)
    this.detailsTarget.hidden = true
    this.actionsTarget.hidden = false
    this.showPanel()
  }

  save(categories) {
    const preferences = {
      version: 1,
      necessary: true,
      analytics: Boolean(categories.analytics),
      marketing: Boolean(categories.marketing),
      updatedAt: new Date().toISOString()
    }

    localStorage.setItem(this.constructor.STORAGE_KEY, JSON.stringify(preferences))
    localStorage.removeItem(this.constructor.LEGACY_STORAGE_KEY)
    this.syncInputs(preferences)
    this.showLauncher()
    this.dispatchChange(preferences)
  }

  showPanel() {
    this.panelTarget.hidden = false
    if (this.hasLauncherTarget) this.launcherTarget.hidden = true
  }

  showLauncher() {
    this.panelTarget.hidden = true
    this.detailsTarget.hidden = true
    this.actionsTarget.hidden = false
    if (this.hasLauncherTarget) this.launcherTarget.hidden = false
  }

  syncInputs(preferences) {
    this.analyticsTarget.checked = preferences.analytics
    this.marketingTarget.checked = preferences.marketing
  }

  dispatchChange(preferences) {
    window.dispatchEvent(new CustomEvent("cookie-consent:changed", {
      detail: { preferences }
    }))
  }

  migrateLegacyPreference() {
    const legacyPreference = localStorage.getItem(this.constructor.LEGACY_STORAGE_KEY)
    if (legacyPreference === null) return null

    const accepted = legacyPreference === "true"
    const preferences = {
      version: 1,
      necessary: true,
      analytics: accepted,
      marketing: accepted,
      updatedAt: new Date().toISOString()
    }

    localStorage.setItem(this.constructor.STORAGE_KEY, JSON.stringify(preferences))
    localStorage.removeItem(this.constructor.LEGACY_STORAGE_KEY)
    return preferences
  }

  get preferences() {
    try {
      const preferences = JSON.parse(localStorage.getItem(this.constructor.STORAGE_KEY))
      if (
        preferences?.version !== 1 ||
        typeof preferences.analytics !== "boolean" ||
        typeof preferences.marketing !== "boolean"
      ) return null

      return preferences
    } catch {
      return null
    }
  }

  get defaultPreferences() {
    return { necessary: true, analytics: false, marketing: false }
  }
}
